"use client";

import React, { useMemo } from "react";
import { AlertCircle, User, Phone, Mail } from "lucide-react";
import { useRouter } from "next/navigation";
import { useLanguage } from "@/contexts/LanguageContext";
import { getStrings } from "@/i18n/dictionaries";

/**
 * Component to display when user profile is incomplete for Flutterwave payments
 * Shows missing fields and provides navigation to profile page
 */
export default function CompleteProfilePrompt({ 
  missingFields = [], 
  message = "Complete your profile to use Flutterwave payments.",
  onClose,
  onSwitchToStripe 
}) {
  const router = useRouter();
  const { locale } = useLanguage();
  const t = useMemo(() => getStrings(locale, "payment"), [locale]);

  // Map field names to user-friendly labels with icons
  const fieldLabels = {
    email: { label: t.email_address, icon: Mail },
    phoneNumber: { label: t.phone_number, icon: Phone },
    firstName: { label: t.first_name, icon: User },
    lastName: { label: t.last_name, icon: User },
  };

  const handleGoToProfile = () => {
    onClose();
    router.push("/profile?tab=personal");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="border-b border-slate-200 px-6 py-4">
          <div className="mb-3 flex items-center justify-center">
            <div className="rounded-full bg-amber-50 p-3">
              <AlertCircle className="h-8 w-8 text-amber-600" />
            </div>
          </div>
          <h2 className="text-center text-xl font-bold text-slate-900">
            {t.complete_profile}
          </h2>
          <p className="mt-2 text-center text-sm text-slate-600">
            {message}
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-amber-900 mb-3">
              {t.add_following}
            </p>
            <ul className="space-y-2">
              {missingFields.map((field) => {
                const fieldInfo = fieldLabels[field] || { label: field, icon: User };
                const Icon = fieldInfo.icon;
                return (
                  <li key={field} className="flex items-center gap-2 text-sm text-amber-900">
                    <Icon className="h-4 w-4" />
                    <span className="font-medium">{fieldInfo.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Navigation buttons */}
          <div className="mt-4 space-y-3">
            <button
              onClick={handleGoToProfile}
              className="w-full rounded-md bg-orange-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 transition"
            >
              {t.go_to_profile}
            </button>

            {onSwitchToStripe && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-300"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-2 text-slate-500 font-medium">{t.or_card_payment}</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    onClose();
                    onSwitchToStripe();
                  }}
                  className="w-full rounded-md bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition"
                >
                  {t.pay_with_card}
                </button>
                <p className="text-xs text-slate-600 text-center">
                  {t.card_no_phone}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4">
          <button
            onClick={onClose}
            className="w-full rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition"
          >
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
}
